import React, { useEffect, useState } from "react";
import {
    SovosBox,
    SovosButton,
    SovosChipFilter,
    SovosCountryFlag,
    SovosTooltip,
    SovosTypography,
} from "mosaic-react";
import { ArrowForward, Block, CheckCircle, ExpandLess, ExpandMore, RadioButtonUnchecked, Warning } from "mosaic-react-icons";
import {
    importProfileStatusDescriptionKey,
    importProfileStatusFromProfile,
} from "../../../utils/importProfileStatus";
import I18n from "../../../I18n";
import { visibleOverviewMappingColumns } from "../MappingFlow/mappedSourceColumns";
import { sanitizeDisplayValue } from "../MappingFlow/mappingDisplayLabels";

const STATUS_ICONS = {
    mapped: <CheckCircle sx={{ color: "success.main", fontSize: 16 }} />,
    data_warnings: <Warning sx={{ color: "warning.main", fontSize: 16 }} />,
    data_errors: <Warning sx={{ color: "error.main", fontSize: 16 }} />,
    warning: <Warning sx={{ color: "warning.main", fontSize: 16 }} />,
    error: <Warning sx={{ color: "error.main", fontSize: 16 }} />,
    skipped: <Block sx={{ color: "text.disabled", fontSize: 16 }} />,
    unmapped: <RadioButtonUnchecked sx={{ color: "text.disabled", fontSize: 16 }} />,
    none: null,
};

const configSetLabel = (configSet) => {
    if (!configSet) return "(No config set)";
    return configSet.replace(/^CSV_/, "");
};

const countryCode = (configSet) => {
    if (!configSet) return null;
    return configSet.replace(/^CSV_/, "");
};

const profileSortScore = (profile) => {
    const status = importProfileStatusFromProfile(profile);
    if (status === "data_errors" || status === "error") return 0;
    if (status === "data_warnings" || status === "warning") return 1;
    if (status === "mapped" || status === "skipped") return 2;
    return 3;
};

const profileStatusIcon = (profile) => {
    const statusKey = importProfileStatusFromProfile(profile);
    if (statusKey === "none") {
        return (
            <SovosTypography variant="body2" color="text.disabled" component="span">
                -
            </SovosTypography>
        );
    }
    const icon = STATUS_ICONS[statusKey] || STATUS_ICONS.unmapped;
    const tooltip = I18n.translate(importProfileStatusDescriptionKey(statusKey));

    return (
        <SovosTooltip title={tooltip} placement="top">
            <span style={{ display: "inline-flex" }}>{icon}</span>
        </SovosTooltip>
    );
};

const slotProfiles = (slot) => {
    const profiles = slot?.profiles || [];
    if (!profiles.length) return [null];

    return [...profiles].sort((a, b) => {
        const scoreDiff = profileSortScore(a) - profileSortScore(b);
        if (scoreDiff !== 0) return scoreDiff;
        return a.profileName.localeCompare(b.profileName);
    });
};

const slotSortScore = (slot) => Math.min(...slotProfiles(slot).map((profile) => profileSortScore(profile)));

const buildSlotKey = (slot, profile) => `${slot.fileType}|${slot.entityName}|${profile?.profileId ?? "unmapped"}`;

const ProgressBar = ({ value, max }) => {
    const pct = max > 0 ? Math.round((value / max) * 100) : 0;
    return (
        <SovosBox sx={{ width: "100%", height: 5, bgcolor: "grey.200", borderRadius: 3, overflow: "hidden" }}>
            <SovosBox
                sx={{
                    height: "100%",
                    width: `${pct}%`,
                    bgcolor: pct === 100 ? "success.main" : "primary.main",
                    borderRadius: 3,
                    transition: "width 0.3s ease",
                }}
            />
        </SovosBox>
    );
};

const CountChip = ({ label, color, bgcolor }) => (
    <SovosBox
        sx={{
            display: "inline-flex",
            alignItems: "center",
            px: 0.75,
            py: 0.25,
            borderRadius: 1.5,
            bgcolor,
        }}
    >
        <SovosTypography variant="caption" color={color} fontWeight={600}>
            {label}
        </SovosTypography>
    </SovosBox>
);

const MandatoryVariantBadges = ({ mandatoryVariants }) => {
    if (!mandatoryVariants || mandatoryVariants.length === 0) {
        return (
            <SovosChipFilter
                label="optional"
                sx={{ height: 20, fontSize: "0.65rem", opacity: 0.6 }}
            />
        );
    }
    return (
        <SovosBox sx={{ display: "flex", gap: 0.25, flexWrap: "wrap" }}>
            {[...mandatoryVariants].sort((a, b) => a.variant.localeCompare(b.variant)).map(({ variant, label }) => (
                <SovosTooltip key={variant} title={`${variant} — ${label}`} placement="top">
                    <span>
                        <SovosChipFilter
                            label={variant}
                            sx={{ height: 20, fontSize: "0.65rem", cursor: "default" }}
                        />
                    </span>
                </SovosTooltip>
            ))}
        </SovosBox>
    );
};

const EntitySlotRow = ({ i18n, slot, profile, isSelected, onSelect }) => {
    const { entityName } = slot;
    const slotKey = buildSlotKey(slot, profile);

    return (
        <SovosBox
            onClick={() => onSelect(slotKey)}
            sx={{
                pl: 3,
                pr: 1.5,
                py: 1,
                cursor: "pointer",
                bgcolor: isSelected ? "action.selected" : "transparent",
                borderLeft: "2px solid",
                borderColor: isSelected ? "primary.main" : "transparent",
                "&:hover": { bgcolor: "action.hover" },
            }}
        >
            <SovosBox sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: profile ? 0.5 : 0 }}>
                {profileStatusIcon(profile)}
                <SovosTypography
                    variant="body2"
                    fontWeight={isSelected ? 600 : 400}
                    color={profile ? "text.primary" : "text.secondary"}
                    noWrap
                    sx={{ flex: 1 }}
                >
                    {profile ? profile.profileName : entityName}
                </SovosTypography>
            </SovosBox>
            {profile && (
                <SovosBox sx={{ pl: 3 }}>
                    <ProgressBar value={profile.mappedColumns} max={profile.totalColumns} />
                    <SovosBox sx={{ display: "flex", alignItems: "center", mt: 0.25, gap: 0.5 }}>
                        <SovosTypography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                            {i18n`${profile.mappedColumns}/${profile.totalColumns} mapped`}
                        </SovosTypography>
                        {profile.errorCount > 0 && (
                            <CountChip label={i18n`${profile.errorCount} errors`} color="error.dark" bgcolor="error.light" />
                        )}
                        {profile.warningCount > 0 && (
                            <CountChip label={i18n`${profile.warningCount} warnings`} color="warning.dark" bgcolor="warning.light" />
                        )}
                    </SovosBox>
                </SovosBox>
            )}
            {!profile && (
                <SovosBox sx={{ pl: 3 }}>
                    <SovosTypography variant="caption" color="text.disabled">
                        {i18n`-`}
                    </SovosTypography>
                </SovosBox>
            )}
        </SovosBox>
    );
};

const EntityGroup = ({ configSet, entities, i18n, selectedSlotKey, onSelect }) => {
    const [expanded, setExpanded] = useState(true);
    const sortedEntities = [...entities].sort(
        (a, b) => slotSortScore(a) - slotSortScore(b),
    );

    return (
        <SovosBox sx={{ mb: 1 }}>
            <SovosBox
                onClick={() => setExpanded((v) => !v)}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.75,
                    px: 1,
                    py: 1,
                    cursor: "pointer",
                    borderRadius: 1,
                    "&:hover": { bgcolor: "action.hover" },
                }}
            >
                {expanded ? (
                    <ExpandLess sx={{ fontSize: 18, color: "text.primary" }} />
                ) : (
                    <ExpandMore sx={{ fontSize: 18, color: "text.primary" }} />
                )}
                {countryCode(configSet) && (
                    <SovosCountryFlag flagHeight="16px" countryCode={countryCode(configSet)} />
                )}
                <SovosTypography variant="body2" fontWeight={700} sx={{ flex: 1 }} noWrap>
                    {configSetLabel(configSet)}
                </SovosTypography>
                <SovosTypography variant="caption" color="text.secondary">
                    {i18n`${entities.filter((s) => (s.profiles || []).length > 0).length}/${entities.length} mapped`}
                </SovosTypography>
            </SovosBox>
            {expanded && sortedEntities.map((slot) => {
                return (
                    <SovosBox key={`${slot.fileType}|${slot.entityName}`}>
                        <SovosBox
                            sx={{
                                px: 1.5,
                                py: 0.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <SovosTypography variant="caption" fontWeight={600} color="text.secondary" noWrap sx={{ flex: 1 }}>
                                {slot.entityName}
                            </SovosTypography>
                            <MandatoryVariantBadges mandatoryVariants={slot.mandatoryVariants} />
                        </SovosBox>
                        {slotProfiles(slot).map((profile) => {
                            const slotKey = buildSlotKey(slot, profile);
                            return (
                                <EntitySlotRow
                                    key={slotKey}
                                    i18n={i18n}
                                    slot={slot}
                                    profile={profile}
                                    isSelected={selectedSlotKey === slotKey}
                                    onSelect={onSelect}
                                />
                            );
                        })}
                    </SovosBox>
                );
            })}
        </SovosBox>
    );
};

const SlotDetail = ({ slot, profile, i18n, locale, projectId, fiscalYear, importId, history }) => {
    const { entityName, mandatoryVariants } = slot;

    const handleEdit = () => {
        if (profile) {
            history.push(`/${locale}/${projectId}/${fiscalYear}/${importId}/column_mapping/${profile.profileId}`);
        }
    };

    const mappingColumns = visibleOverviewMappingColumns(profile?.columns);

    if (!profile) {
        return (
            <SovosBox sx={{ p: 4, textAlign: "center" }}>
                <RadioButtonUnchecked sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
                <SovosTypography variant="h6" fontWeight={600} color="text.secondary" gutterBottom>
                    {entityName}
                </SovosTypography>
                <SovosBox sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                    <MandatoryVariantBadges mandatoryVariants={mandatoryVariants} />
                </SovosBox>
                <SovosTypography variant="body2" color="text.secondary">
                    {i18n`No import profile has been mapped to this entity yet.`}
                </SovosTypography>
                <SovosTypography variant="body2" color="text.secondary">
                    Upload a CSV file to create an import profile, then map it here.
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <>
            <SovosBox sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 2 }}>
                <SovosBox sx={{ flex: 1, mr: 2 }}>
                    <SovosTypography variant="h6" fontWeight={700}>
                        {profile.profileName}
                    </SovosTypography>
                    <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.25 }}>
                        <SovosTypography variant="body2" color="text.secondary">
                            {entityName}
                            {profile.configSet ? ` · ${configSetLabel(profile.configSet)}` : ""}
                        </SovosTypography>
                        <MandatoryVariantBadges mandatoryVariants={mandatoryVariants} />
                    </SovosBox>
                </SovosBox>
                <SovosButton
                    variant="contained"
                    size="small"
                    endIcon={<ArrowForward sx={{ fontSize: 16 }} />}
                    onClick={handleEdit}
                >
                    Edit Mapping
                </SovosButton>
            </SovosBox>

            <SovosBox sx={{ mb: 3 }}>
                <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <SovosTypography variant="caption" color="text.secondary">
                        {i18n`${profile.mappedColumns} / ${profile.totalColumns} columns mapped`}
                    </SovosTypography>
                    <SovosBox sx={{ display: "flex", gap: 0.5 }}>
                        {profile.errorCount > 0 && (
                            <CountChip label={i18n`${profile.errorCount} errors`} color="error.dark" bgcolor="error.light" />
                        )}
                        {profile.warningCount > 0 && (
                            <CountChip label={i18n`${profile.warningCount} warnings`} color="warning.dark" bgcolor="warning.light" />
                        )}
                    </SovosBox>
                </SovosBox>
                <ProgressBar value={profile.mappedColumns} max={profile.totalColumns} />
            </SovosBox>

            {mappingColumns.length > 0 && (
                <SovosBox>
                    <SovosBox
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 28px 1fr",
                            gap: 1,
                            px: 1.5,
                            py: 0.75,
                            bgcolor: "grey.50",
                            borderRadius: 1,
                            mb: 0.5,
                        }}
                    >
                        <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                            Source column
                        </SovosTypography>
                        <SovosBox />
                        <SovosTypography variant="caption" fontWeight={600} color="text.secondary">
                            Target field
                        </SovosTypography>
                    </SovosBox>
                    {mappingColumns.map((col, idx) => (
                        <SovosBox
                            key={idx}
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 28px 1fr",
                                gap: 1,
                                px: 1.5,
                                py: 0.75,
                                borderBottom: "1px solid",
                                borderColor: "divider",
                                alignItems: "center",
                            }}
                        >
                            <SovosTypography variant="body2" noWrap>
                                {col.sourceColumn}
                            </SovosTypography>
                            <SovosBox sx={{ display: "flex", justifyContent: "center" }}>
                                {STATUS_ICONS[col.status] || STATUS_ICONS.skipped}
                            </SovosBox>
                            <SovosTypography variant="body2" color="text.secondary" noWrap>
                                {sanitizeDisplayValue(col.targetField) || "—"}
                            </SovosTypography>
                        </SovosBox>
                    ))}
                </SovosBox>
            )}

            {(profile.unmappedMasterFields || []).length > 0 && (
                <SovosBox sx={{ mt: 2 }}>
                    <SovosTypography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: "block", mb: 1 }}>
                        Not covered from master schema
                    </SovosTypography>
                    <SovosBox sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {profile.unmappedMasterFields.map((fieldName) => (
                            <SovosBox
                                key={fieldName}
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    bgcolor: "grey.100",
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <SovosTypography variant="caption" sx={{ color: "text.secondary" }}>
                                    {sanitizeDisplayValue(fieldName)}
                                </SovosTypography>
                            </SovosBox>
                        ))}
                    </SovosBox>
                </SovosBox>
            )}
        </>
    );
};

const firstSlotKey = (configSets) => {
    for (const cs of configSets) {
        if (cs.entities?.length > 0) {
            const first = cs.entities[0];
            return buildSlotKey(first, slotProfiles(first)[0]);
        }
    }
    return null;
};

const findSelectedSlot = (configSets, slotKey) => {
    for (const cs of configSets) {
        for (const slot of cs.entities || []) {
            for (const profile of slotProfiles(slot)) {
                if (buildSlotKey(slot, profile) === slotKey) {
                    return { slot, profile };
                }
            }
        }
    }
    return null;
};

const MappingOverviewView = ({ configSets = [], i18n, loading, locale, projectId, fiscalYear, importId, history }) => {
    const [selectedSlotKey, setSelectedSlotKey] = useState(() => firstSlotKey(configSets));
    const selectedSlot = findSelectedSlot(configSets, selectedSlotKey);

    useEffect(() => {
        if (!selectedSlotKey || !selectedSlot) {
            setSelectedSlotKey(firstSlotKey(configSets));
        }
    }, [configSets, selectedSlot, selectedSlotKey]);

    if (!loading && configSets.length === 0) {
        return (
            <SovosBox sx={{ p: 4, textAlign: "center" }}>
                <SovosTypography variant="body1" color="text.secondary">
                    No import profiles found. Upload a CSV file to get started.
                </SovosTypography>
            </SovosBox>
        );
    }

    return (
        <SovosBox sx={{ display: "flex", height: "100%", overflow: "hidden" }}>
            <SovosBox
                sx={{
                    width: "32%",
                    minWidth: 240,
                    borderRight: "1px solid",
                    borderColor: "divider",
                    overflowY: "auto",
                    py: 1,
                }}
            >
                {configSets.map((cs) => (
                    <EntityGroup
                        key={cs.configSet}
                        configSet={cs.configSet}
                        entities={cs.entities || []}
                        i18n={i18n}
                        selectedSlotKey={selectedSlotKey}
                        onSelect={setSelectedSlotKey}
                    />
                ))}
            </SovosBox>

            <SovosBox sx={{ flex: 1, overflowY: "auto", p: 3 }}>
                {selectedSlot ? (
                    <SlotDetail
                        slot={selectedSlot.slot}
                        profile={selectedSlot.profile}
                        i18n={i18n}
                        locale={locale}
                        projectId={projectId}
                        fiscalYear={fiscalYear}
                        importId={importId}
                        history={history}
                    />
                ) : (
                    <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                        <SovosTypography variant="body2" color="text.secondary">
                            Select an entity from the list to view its mapping.
                        </SovosTypography>
                    </SovosBox>
                )}
            </SovosBox>
        </SovosBox>
    );
};

export default MappingOverviewView;
